import type { ThemeModule } from "../modules/themes/theme.types";
import type { ProfileRole, AgeBand } from "../types/profiles";

export interface AccessProfile {
  role?: ProfileRole;
  ageBand?: AgeBand;
}

const AGE_ORDER: AgeBand[] = ["6-8", "9-11", "12-15", "16-plus"];

function ageBandGte(candidate?: AgeBand, min?: AgeBand): boolean {
  if (!min) return true;
  if (!candidate) return true;
  const candidateIndex = AGE_ORDER.indexOf(candidate);
  const minIndex = AGE_ORDER.indexOf(min);
  if (candidateIndex === -1 || minIndex === -1) return true;
  return candidateIndex >= minIndex;
}

/**
 * Indique si un profil donné peut accéder à un thème.
 * Règles :
 * 1. Profil absent → mode démo permissif.
 * 2. Si minAgeBand + ageBand profil → comparer en respectant l'ordre des tranches.
 * 3. Si allowedRoles + rôle profil → le rôle doit être dans allowedRoles.
 * 4. Si une info manque côté profil (ageBand ou rôle) → on reste permissif.
 */
export function canAccessTheme(
  theme: ThemeModule,
  profile?: AccessProfile | null
): boolean {
  if (!profile) return true;

  const ageOk = ageBandGte(profile.ageBand, theme.minAgeBand);
  if (!ageOk) return false;

  if (theme.allowedRoles && theme.allowedRoles.length > 0) {
    if (profile.role && theme.allowedRoles.includes(profile.role)) {
      return true;
    }
    // Rôle manquant ou non autorisé → si le profil n'a pas de rôle, on reste permissif.
    return profile.role ? false : true;
  }

  return true;
}

export function filterThemesForProfile(
  themes: ThemeModule[],
  profile?: AccessProfile | null
): ThemeModule[] {
  return themes.filter((theme) => canAccessTheme(theme, profile));
}

// TODO MixMind: étendre la logique d'accès aux exercices individuels (micro-cours, quiz, mini-jeux).
// TODO MixMind: connecter un sélecteur d'âge/rôle (page Compte) pour prévisualiser l'accès.
